import { Divider, makeStyles, Typography } from '@material-ui/core';
import { groupBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext } from 'react';
import { DataTableContext } from '../../contexts';
import { colors } from '../../themes';
import Item from './Item';

type TableConfig = {
  key: string;
  label: string;
  group: string;
};

const TABLES_CONFIG: TableConfig[] = [
  { key: 'all', label: 'All', group: 'Summary' },
  { key: 'active', label: 'Active', group: 'Summary' },
  { key: 'delivered', label: 'Delivered', group: 'Summary' },
  { key: 'completed', label: 'Dispatched Cleared', group: 'Summary' },
  { key: 'rejected', label: 'Rejected', group: 'Summary' },

  { key: 'assignment_unassigned', label: 'Unassigned', group: 'Assignments' },
  { key: 'assignment_assigned', label: 'Assigned', group: 'Assignments' },
  { key: 'stale', label: 'Stale', group: 'Assignments' },

  { key: 'pickup_arrived', label: 'Pickup Arrived', group: 'Pick-up' },
  { key: 'pickup_missed', label: 'Pickup Delayed', group: 'Pick-up' },
  { key: 'dropoff_arrived', label: 'Dropoff Arrived', group: 'Drop-off' },
  { key: 'dropoff_missed', label: 'Dropoff Delayed', group: 'Drop-off' },
  {
    key: 'dispatch_processing',
    label: 'Dispatch Processing',
    group: 'Billing',
  },
  {
    key: 'dispatch_verified',
    label: 'Dispatch Verified',
    group: 'Billing',
  },
  {
    key: 'billing_issue',
    label: 'Billing Issue',
    group: 'Billing',
  },
  { key: 'invoiced', label: 'Invoiced', group: 'Billing' },

  {
    key: 'additional_billing_all',
    label: 'Additional Billing - All',
    group: 'Additional Billing',
  },
  {
    key: 'additional_billing_pending',
    label: 'Additional Billing - Pending',
    group: 'Additional Billing',
  },
  {
    key: 'additional_billing_billing_issue',
    label: 'Additional Billing - Issue',
    group: 'Additional Billing',
  },
  {
    key: 'additional_billing_approved',
    label: 'Additional Billing - Approved',
    group: 'Additional Billing',
  },
  {
    key: 'additional_billing_denied',
    label: 'Additional Billing - Denied',
    group: 'Additional Billing',
  },
  {
    key: 'additional_billing_invoiced',
    label: 'Additional Billing - Invoiced',
    group: 'Additional Billing',
  },
];

const ALWAYS_ACTIVE_GROUPS = ['Billing', 'Additional Billing'];
type Props = {};

const ActiveTables: React.FC<Props> = () => {
  const classes = useStyles();
  const { presetTables, saveVisibleTables } = useContext(DataTableContext);

  // Split tables into active (visible) and available (hidden)
  const activeTables = TABLES_CONFIG.filter(
    table =>
      ALWAYS_ACTIVE_GROUPS.includes(table.group) ||
      presetTables?.[table.key]?.is_visible,
  );

  const availableTables = TABLES_CONFIG.filter(
    table =>
      !ALWAYS_ACTIVE_GROUPS.includes(table.group) &&
      !presetTables?.[table.key]?.is_visible,
  );

  // Group available tables by their assigned group
  const groupedAvailableTables = groupBy(availableTables, 'group');

  const handleToggle = useCallback(
    (tableName: string) => {
      // Prevent toggling off items in locked groups
      const tableConfig = TABLES_CONFIG.find(t => t.key === tableName);
      if (tableConfig && ALWAYS_ACTIVE_GROUPS.includes(tableConfig.group)) {
        return;
      }

      const activeKeys = activeTables.map(t => t.key);
      let updatedVisibleTables: string[];

      if (activeKeys.includes(tableName)) {
        updatedVisibleTables = activeKeys.filter(key => key !== tableName);
      } else {
        updatedVisibleTables = [...activeKeys, tableName];
      }

      saveVisibleTables(updatedVisibleTables);
    },
    [activeTables, saveVisibleTables],
  );
  return (
    <>
      <Divider className={classes.divider} />
      <Typography component="h3" variant="subtitle1" className={classes.header}>
        Active Filters
      </Typography>

      <ul className={classes.columnList}>
        {activeTables.map(table => (
          <Item
            key={table.key}
            tableName={table.key}
            label={table.label}
            isVisible={true}
            disabled={ALWAYS_ACTIVE_GROUPS.includes(table.group)}
            handleToggle={handleToggle}
          />
        ))}
      </ul>

      <Divider className={classes.divider} />

      <Typography component="h3" variant="subtitle1" className={classes.header}>
        Available Filters
      </Typography>

      {availableTables.length === 0 ? (
        <div className={classes.noAvailable}>All tables are active.</div>
      ) : (
        Object.keys(groupedAvailableTables).map(groupLabel => (
          <ul key={groupLabel} className={classes.columnListWrapper}>
            <li>{groupLabel}</li>
            <ul className={classes.columnList}>
              {groupedAvailableTables[groupLabel].map(table => (
                <Item
                  key={table.key}
                  tableName={table.key}
                  label={table.label}
                  isVisible={false}
                  handleToggle={handleToggle}
                />
              ))}
            </ul>
          </ul>
        ))
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  header: {
    color: colors.primary[900],
    // Removed float: 'left' to prevent layout collapsing
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    userSelect: 'none',
  },
  divider: {
    margin: theme.spacing(2, -3),
    clear: 'both',
  },
  columnListWrapper: {
    clear: 'both',
    listStyle: 'none',
    margin: 0,
    padding: 0,

    '& > li:first-child': {
      ...theme.typography.h6,
      backgroundColor: colors.primary[100],
      color: colors.primary[900],
      cursor: 'default',
      fontWeight: 500,
      lineHeight: 1,
      padding: theme.spacing(1),
      textTransform: 'uppercase',
    },
  },
  columnList: {
    backgroundColor: 'white',
    borderColor: theme.palette.grey[500],
    borderStyle: 'solid',
    borderWidth: 1,
    clear: 'both',
    listStyleType: 'none',
    margin: 0,
    padding: 0,

    '& + $columnList': {
      marginTop: -1,
    },

    '& li': {
      ...theme.typography.body1,
      alignItems: 'center',
      backgroundColor: 'white',
      borderColor: theme.palette.grey[500],
      borderStyle: 'solid',
      borderWidth: '0 0 1px 0',
      color: colors.primary[900],
      display: 'flex',
      width: '100%', // Ensures every list item fills the full width
      fontWeight: 500,
      textTransform: 'uppercase',

      '&:last-child': {
        borderBottomWidth: 0,
      },
    },
  },
  noAvailable: {
    ...theme.typography.body1,
    clear: 'both',
    color: theme.palette.grey[500],
    fontWeight: 500,
    padding: 0,
  },
}));

export default observer(ActiveTables);
